import React from 'react';
import { Card, CardContent, Typography, Box, Chip } from '@mui/material';
import {
  Note,
  Dashboard as CorkboardIcon,
  Timeline as TimelineIcon,
  Category as CategoryIcon,
} from '@mui/icons-material';
import PropTypes from 'prop-types';

const NoteCard = ({ note, onClick, showSource = false, showCategory = false }) => {
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = typeof timestamp === 'number' ? new Date(timestamp) : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  const truncateText = (text, maxLength = 150) => {
    if (!text) return '';
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  // Get source display info
  const getSourceInfo = () => {
    if (!note.source) return null;
    if (note.source.type === 'corkboard') {
      return {
        label: `Corkboard: ${note.source.name}`,
        icon: CorkboardIcon,
        color: 'warning',
      };
    }
    if (note.source.type === 'timeline') {
      return {
        label: `Timeline: ${note.source.name}`,
        icon: TimelineIcon,
        color: 'secondary',
      };
    }
    return null;
  };

  const sourceInfo = getSourceInfo();

  return (
    <Card
      variant="outlined"
      sx={{
        cursor: 'pointer',
        transition: 'transform 0.2s, box-shadow 0.2s',
        backgroundColor: note.color || 'background.paper',
        width: '100%',
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 3,
        },
      }}
      onClick={() => onClick(note)}
    >
      <CardContent
        sx={{
          p: 2,
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          minHeight: 0,
          '&:last-child': { pb: 2 },
        }}
      >
        {/* Source and Category Labels */}
        {(showSource || showCategory) && (sourceInfo || note.category) && (
          <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap', minWidth: 0 }}>
            {showSource && sourceInfo && (
              <Chip
                icon={<sourceInfo.icon sx={{ fontSize: 16 }} />}
                label={sourceInfo.label}
                size="small"
                color={sourceInfo.color}
                variant="outlined"
                sx={{ fontSize: '0.7rem', height: 24, maxWidth: '100%' }}
              />
            )}
            {showCategory && note.category && (
              <Chip
                icon={<CategoryIcon sx={{ fontSize: 16 }} />}
                label={note.category}
                size="small"
                color="info"
                variant="filled"
                sx={{ fontSize: '0.7rem', height: 24, maxWidth: '100%' }}
              />
            )}
          </Box>
        )}

        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1, minWidth: 0 }}>
          <Note color="primary" sx={{ fontSize: 20, mt: 0.25, flexShrink: 0 }} />
          <Box sx={{ flexGrow: 1, minWidth: 0, overflow: 'hidden' }}>
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {note.title || 'Untitled Note'}
            </Typography>
            {note.createdAt && (
              <Typography variant="caption" color="text.secondary">
                {formatDate(note.createdAt)}
              </Typography>
            )}
          </Box>
        </Box>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            flexGrow: 1,
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            hyphens: 'auto',
            whiteSpace: 'pre-wrap',
            display: '-webkit-box',
            WebkitLineClamp: 4,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            lineHeight: 1.5,
          }}
        >
          {truncateText(note.content, 200)}
        </Typography>
        {note.tags && note.tags.length > 0 && (
          <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
            {note.tags.slice(0, 3).map((tag, index) => (
              <Typography
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                variant="caption"
                sx={{
                  backgroundColor: 'primary.light',
                  color: 'primary.contrastText',
                  px: 1,
                  py: 0.25,
                  borderRadius: 0.5,
                  fontSize: '0.75rem',
                }}
              >
                {tag}
              </Typography>
            ))}
            {note.tags.length > 3 && (
              <Typography variant="caption" color="text.secondary">
                +{note.tags.length - 3} more
              </Typography>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

NoteCard.propTypes = {
  note: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    content: PropTypes.string,
    color: PropTypes.string,
    createdAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    tags: PropTypes.arrayOf(PropTypes.string),
    category: PropTypes.string,
    source: PropTypes.shape({
      type: PropTypes.oneOf(['corkboard', 'timeline', 'standalone']),
      name: PropTypes.string,
      id: PropTypes.string,
    }),
  }).isRequired,
  onClick: PropTypes.func.isRequired,
  showSource: PropTypes.bool,
  showCategory: PropTypes.bool,
};

NoteCard.defaultProps = {
  showSource: false,
  showCategory: false,
};

export default NoteCard;
